import { useEffect, useMemo, useState } from 'react';
import { MarketIndicatorTransformationType } from '../../../../../../../Generated/Raven-Demeter';
import NumberInput from '../../../../../../Components/Form/Inputs/NumberInput';
import RegexValidators from '../../../../../../Core/Validation/RegexValidators';
import useLanguage from '../../../../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../../../../Services/Tracking/TrackingService';
import styles from '../../MarketIndicatorFactorPage.module.scss';

interface IDataSelectorTransformationValueTextInputProps {
    transformationValue: number;
    handleChange: (transformationValue: number) => void;
    transformationType?: MarketIndicatorTransformationType;
    track: TrackingEvent;
}

const defaultValueForRollingAverageForwardContracts = 2;
const defaultValueForRollingAverage = 3;
const defaultValueForRelativeStrengthIndex = 5;
const defaultValueForMultiply = 3;

const DataSelectorTransformationValueTextInput: React.FC<IDataSelectorTransformationValueTextInputProps> = (
    props: IDataSelectorTransformationValueTextInputProps,
) => {
    const [translations] = useLanguage();
    const [validation, setValidation] = useState(RegexValidators.PositiveOrNegativeNumber);
    const [errorMessage, setErrorMessage] = useState(translations.marketIndicatorsManagement.messages.positiveOrNegativeNumber);
    const [defaultTransformationValue, setDefaultTransformationValue] = useState<number>();
    const numberInRange = useMemo(() => validation.test(`${props.transformationValue}`), [props.transformationType]);

    const transformationTypeIsRollingAverageForwardContracts = props.transformationType === MarketIndicatorTransformationType.RollingAverageForwardContracts;
    const transformationTypeIsRollingAverage = props.transformationType === MarketIndicatorTransformationType.RollingAverage;
    const transformationTypeIsRelativeStrengthIndex = props.transformationType === MarketIndicatorTransformationType.RelativeStrengthIndex;
    const transformationTypeIsMultiply = props.transformationType === MarketIndicatorTransformationType.Multiply;

    useEffect(() => {
        if (transformationTypeIsMultiply) {
            setValidation(RegexValidators.PositiveNumberThreeOrGreater);
            setErrorMessage(translations.chartBuilder.messages.positiveNumberThreeOrGreater);
            setDefaultTransformationValue(numberInRange ? undefined : defaultValueForMultiply);
        }

        if (transformationTypeIsRollingAverageForwardContracts) {
            setValidation(RegexValidators.PositiveNumberTwoThroughFive);
            setErrorMessage(translations.chartBuilder.messages.positiveNumberTwoThroughFive);
            setDefaultTransformationValue(numberInRange ? undefined : defaultValueForRollingAverageForwardContracts);
        }

        if (transformationTypeIsRollingAverage) {
            setValidation(RegexValidators.PositiveNumberThreeOrGreater);
            setErrorMessage(translations.chartBuilder.messages.positiveNumberThreeOrGreater);
            setDefaultTransformationValue(numberInRange ? undefined : defaultValueForRollingAverage);
        }

        if (transformationTypeIsRelativeStrengthIndex) {
            setValidation(RegexValidators.PositiveNumberFiveOrGreater);
            setErrorMessage(translations.chartBuilder.messages.positiveNumberFiveOrGreater);
            setDefaultTransformationValue(defaultValueForRelativeStrengthIndex);
        }
    }, [props.transformationType, numberInRange]);

    useEffect(() => {
        if (!props.transformationType || numberInRange || !defaultTransformationValue) {
            return;
        }

        props.handleChange(defaultTransformationValue);
    }, [defaultTransformationValue]);

    return (
        <div className={styles.indicator_add_and_edit_input_wrapper}>
            <NumberInput
                title={translations.marketIndicatorsManagement.fields.transformationValue}
                required
                value={props.transformationValue}
                handleTextChange={props.handleChange}
                defaultValue={defaultTransformationValue}
                validation={validation}
                errorMessage={errorMessage}
                track={props.track}
            />
        </div>
    );
};

export default DataSelectorTransformationValueTextInput;
